import PopupTemplate from '@arcgis/core/PopupTemplate';
import Graphic from '@arcgis/core/Graphic';
import type { Geometry, Position } from 'geojson';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';
import {
  isFillSymbol,
  isUscgSymbol,
  USCG_SYMBOLOGY_OPTIONS,
} from './uscgSymbology';

export type LocationCustomField = {
  label: string;
  value: string;
};

export type MapFeatureDetails = {
  name?: string;
  description?: string;
  resourceType?: string;
  customFields?: LocationCustomField[];
};

export function collectCustomFields(
  fields: { label?: string; value?: string }[] | undefined | null
): LocationCustomField[] {
  return (fields ?? [])
    .map((field) => ({
      label: field.label?.trim() ?? '',
      value: field.value?.trim() ?? '',
    }))
    .filter((field) => field.label || field.value);
}

export function readLocationCustomFields(
  properties: unknown
): LocationCustomField[] {
  if (!properties || typeof properties !== 'object') return [];
  const raw = (properties as { customFields?: unknown }).customFields;
  if (!Array.isArray(raw)) return [];
  return collectCustomFields(
    raw.filter(
      (field): field is { label?: string; value?: string } =>
        !!field && typeof field === 'object'
    )
  );
}

export type StoredField = { label: string; value: string };

function present(value: unknown): value is string | number {
  if (typeof value === 'number') return Number.isFinite(value);
  return typeof value === 'string' && value.trim().length > 0;
}

function pushField(fields: StoredField[], label: string, value: unknown) {
  if (!present(value)) return;
  fields.push({ label, value: String(value).trim() });
}

function percent(value: number): string {
  return `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%`;
}

function formatPosition(position: Position): string {
  const [longitude, latitude] = position;
  return `${latitude}, ${longitude}`;
}

function geometryFields(geometry: Geometry | null | undefined): StoredField[] {
  if (!geometry) return [];
  if (geometry.type === 'Point') {
    const [longitude, latitude] = geometry.coordinates;
    return [
      { label: 'Latitude', value: String(latitude) },
      { label: 'Longitude', value: String(longitude) },
    ];
  }
  if (geometry.type === 'LineString') {
    return [
      { label: 'Geometry', value: 'Line' },
      {
        label: 'Coordinates',
        value: geometry.coordinates.map(formatPosition).join('; '),
      },
    ];
  }
  if (geometry.type === 'Polygon') {
    const rings = geometry.coordinates.flatMap((ring) =>
      ring.map(formatPosition)
    );
    return [
      { label: 'Geometry', value: 'Polygon' },
      { label: 'Coordinates', value: rings.join('; ') },
    ];
  }
  return [{ label: 'Geometry', value: geometry.type }];
}

function symbolLabel(resourceType: string): string {
  return (
    USCG_SYMBOLOGY_OPTIONS.find((option) => option.type === resourceType)
      ?.label ?? resourceType
  );
}

/** Every value stored on an internal layer object, in display order. */
export function locationStoredFields(location: {
  name?: string | null;
  description?: string | null;
  resourceType?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  geometry?: Geometry | null;
  properties?: unknown;
}): StoredField[] {
  const fields: StoredField[] = [];
  pushField(fields, 'Name', location.name);
  pushField(fields, 'Description', location.description);
  if (present(location.resourceType)) {
    pushField(fields, 'Symbol', symbolLabel(location.resourceType));
  }
  if (location.createdAt) {
    pushField(fields, 'Created', formatDateTime(location.createdAt));
  }
  if (location.updatedAt) {
    pushField(fields, 'Last updated', formatDateTime(location.updatedAt));
  }
  fields.push(...geometryFields(location.geometry));

  const properties =
    location.properties && typeof location.properties === 'object'
      ? (location.properties as Record<string, unknown>)
      : {};
  if (typeof properties.pointColor === 'string') {
    pushField(fields, 'Point color', properties.pointColor);
  }
  if (typeof properties.pointOpacity === 'number') {
    pushField(fields, 'Point opacity', percent(properties.pointOpacity));
  }
  if (typeof properties.polygonColor === 'string') {
    pushField(fields, 'Polygon color', properties.polygonColor);
  }
  if (typeof properties.polygonOpacity === 'number') {
    pushField(fields, 'Polygon opacity', percent(properties.polygonOpacity));
  }
  for (const field of readLocationCustomFields(properties)) {
    pushField(fields, field.label || 'Field', field.value);
  }
  return fields;
}

/** Scalar attributes on an ArcGIS feature, in key order. */
export function attributeStoredFields(
  attributes: Record<string, unknown> | null | undefined
): StoredField[] {
  if (!attributes) return [];
  return Object.entries(attributes)
    .filter(([, value]) => present(value))
    .map(([label, value]) => ({ label, value: String(value).trim() }));
}

export function storedFieldAttributes(
  fields: StoredField[]
): Record<string, string> {
  return Object.fromEntries(
    fields.map((field, index) => [`stored_${index}`, field.value])
  );
}

export function storedFieldsPopupTemplate(
  title: string,
  fields: StoredField[]
): PopupTemplate {
  return new PopupTemplate({
    title: title || 'Feature',
    content: [
      {
        type: 'fields',
        fieldInfos: fields.map((field, index) => ({
          fieldName: `stored_${index}`,
          label: field.label,
        })),
      },
    ],
  });
}

export function customFieldAttributes(
  fields: LocationCustomField[]
): Record<string, string> {
  return Object.fromEntries(
    fields.map((field, index) => [`customField_${index}`, field.value])
  );
}

export function locationPopupTemplate({
  customFields,
  includeResourceType = true,
}: {
  customFields: LocationCustomField[];
  includeResourceType?: boolean;
}): PopupTemplate {
  return new PopupTemplate({
    title: '{name}',
    content: [
      {
        type: 'text',
        text: '<b>Incident:</b> {incidentName} <br/>',
      },
      {
        type: 'fields',
        fieldInfos: [
          { fieldName: 'name', label: 'Name' },
          { fieldName: 'incidentName', label: 'Incident' },
          ...(includeResourceType
            ? [{ fieldName: 'resourceType', label: 'Resource Type' }]
            : []),
          { fieldName: 'description', label: 'Description' },
          ...customFields.map((field, index) => ({
            fieldName: `customField_${index}`,
            label: field.label || 'Field',
          })),
        ],
      },
    ],
  });
}

export function decorateMapGraphic(
  graphic: Graphic,
  details?: MapFeatureDetails | null
) {
  if (!details) return;
  const customFields = collectCustomFields(details.customFields);
  const resourceType = details.resourceType;
  const includeResourceType =
    resourceType && isUscgSymbol(resourceType)
      ? !isFillSymbol(resourceType)
      : true;
  graphic.attributes = {
    ...(graphic.attributes ?? {}),
    name: details.name || graphic.attributes?.name || 'Feature',
    description: details.description ?? '',
    resourceType: details.resourceType,
    ...customFieldAttributes(customFields),
  };
  graphic.popupTemplate = locationPopupTemplate({
    customFields,
    includeResourceType,
  });
}
